/**
 * Team kit types and default. Kit colors for matches come from the DB (teams.kitPrimary etc.).
 * When no DB kit is available, we show the default grey shirt.
 */

export type TeamKitType =
  | 'plain'
  | 'stripes'
  | 'hoops'
  | 'quarters'
  | 'vertical-split'
  | 'pinstripes'
  | 'sash'
  | 'shoulder-panel'
  | 'half-and-half';

/** DB/API may send `stripe`, `solid`, etc.; map to app kit types. Unknown → plain (sleeves SVG). */
export function normalizeTeamKitType(type: string | null | undefined): TeamKitType {
  if (type == null || typeof type !== 'string') return 'plain';
  const t = type.trim().toLowerCase();
  if (t === 'stripe' || t === 'stripes') return 'stripes';
  if (t === 'solid') return 'plain';
  if (
    t === 'plain' ||
    t === 'hoops' ||
    t === 'quarters' ||
    t === 'vertical-split' ||
    t === 'pinstripes' ||
    t === 'sash' ||
    t === 'shoulder-panel' ||
    t === 'half-and-half'
  ) {
    return t;
  }
  return 'plain';
}

export interface TeamKitConfig {
  primary: string;
  secondary: string;
  type: TeamKitType;
}

/** Light gray outline so white kits are visible on white backgrounds. */
export const KIT_OUTLINE_COLOR = '#D1D5DB';

/** Default grey shirt when team has no kit in DB (always show SVG kit, never a dot). */
export const DEFAULT_TEAM_KIT_CONFIG: TeamKitConfig = {
  primary: '#6B7280',
  secondary: '#9CA3AF',
  type: 'plain',
};

export const TEAM_KIT_BY_NAME: Record<string, TeamKitConfig> = {

  // ---------- English Premier League (20 Teams) ----------
  'arsenal': { primary: '#EF0107', secondary: '#FFFFFF', type: 'plain' },
  'aston villa': { primary: '#670E36', secondary: '#95BFE5', type: 'plain' },
  'bournemouth': { primary: '#E32221', secondary: '#000000', type: 'stripes' },
  'brentford': { primary: '#E32221', secondary: '#FFFFFF', type: 'stripes' },
  'brighton': { primary: '#0066B3', secondary: '#FFFFFF', type: 'stripes' },
  'burnley': { primary: '#8B1538', secondary: '#F9D616', type: 'plain' },
  'chelsea': { primary: '#034694', secondary: '#FFFFFF', type: 'plain' },
  'crystal palace': { primary: '#1B458F', secondary: '#C4122E', type: 'stripes' },
  'everton': { primary: '#003399', secondary: '#FFFFFF', type: 'plain' },
  'fulham': { primary: '#FFFFFF', secondary: '#000000', type: 'plain' },
  'leeds': { primary: '#FFFFFF', secondary: '#003399', type: 'plain' },
  'liverpool': { primary: '#C8102E', secondary: '#FFFFFF', type: 'plain' },
  'manchester city': { primary: '#6CABDD', secondary: '#FFFFFF', type: 'plain' },
  'manchester united': { primary: '#DA291C', secondary: '#FFFFFF', type: 'plain' },
  'newcastle': { primary: '#000000', secondary: '#FFFFFF', type: 'stripes' },
  'nottingham forest': { primary: '#DD0000', secondary: '#FFFFFF', type: 'plain' },
  'sunderland': { primary: '#E32221', secondary: '#FFFFFF', type: 'stripes' },
  'tottenham': { primary: '#FFFFFF', secondary: '#132257', type: 'plain' },
  'west ham': { primary: '#7A263B', secondary: '#2DAFE5', type: 'plain' },
  'wolves': { primary: '#FDB913', secondary: '#000000', type: 'plain' },

  // ---------- Spanish La Liga (20 Teams) ----------
  'alaves': { primary: '#00519C', secondary: '#FFFFFF', type: 'stripes' },
  'athletic club': { primary: '#E32221', secondary: '#FFFFFF', type: 'stripes' },
  'atletico madrid': { primary: '#CB3524', secondary: '#FFFFFF', type: 'stripes' },
  'barcelona': { primary: '#A50044', secondary: '#004D98', type: 'stripes' },
  'celta vigo': { primary: '#67B7E3', secondary: '#E53233', type: 'plain' },
  'elche': { primary: '#00914C', secondary: '#FFFFFF', type: 'plain' },
  'espanyol': { primary: '#004D98', secondary: '#FFFFFF', type: 'stripes' },
  'getafe': { primary: '#0C2340', secondary: '#00B140', type: 'plain' },
  'girona': { primary: '#E63946', secondary: '#FFFFFF', type: 'plain' },
  'levante': { primary: '#E32221', secondary: '#0066B3', type: 'stripes' },
  'mallorca': { primary: '#FF0000', secondary: '#000000', type: 'stripes' },
  'osasuna': { primary: '#8B1538', secondary: '#003366', type: 'plain' },
  'oviedo': { primary: '#0066B3', secondary: '#FFFFFF', type: 'plain' },
  'rayo vallecano': { primary: '#C8102E', secondary: '#FFFFFF', type: 'plain' },
  'real betis': { primary: '#00914C', secondary: '#FFFFFF', type: 'stripes' },
  'real madrid': { primary: '#FFFFFF', secondary: '#FEBE10', type: 'plain' },
  'real sociedad': { primary: '#0066B3', secondary: '#FFFFFF', type: 'stripes' },
  'sevilla': { primary: '#C8102E', secondary: '#FFFFFF', type: 'plain' },
  'valencia': { primary: '#FFFFFF', secondary: '#000000', type: 'plain' },
  'villarreal': { primary: '#FFCD00', secondary: '#005EB8', type: 'plain' },

  // ---------- Italian Serie A (20 Teams) ----------
  'ac milan': { primary: '#FB090B', secondary: '#000000', type: 'stripes' },
  'as roma': { primary: '#8E1F2F', secondary: '#F9D616', type: 'plain' },
  'atalanta': { primary: '#005293', secondary: '#000000', type: 'stripes' },
  'bologna': { primary: '#A6192E', secondary: '#003366', type: 'stripes' },
  'cagliari': { primary: '#E63946', secondary: '#003366', type: 'plain' },
  'como': { primary: '#0055A4', secondary: '#FFFFFF', type: 'plain' },
  'cremonese': { primary: '#4B5563', secondary: '#E32221', type: 'plain' },
  'fiorentina': { primary: '#482E92', secondary: '#FFFFFF', type: 'plain' },
  'genoa': { primary: '#AD0A0A', secondary: '#003366', type: 'quarters' },
  'inter': { primary: '#003399', secondary: '#000000', type: 'stripes' },
  'juventus': { primary: '#FFFFFF', secondary: '#000000', type: 'stripes' },
  'lazio': { primary: '#87CEEB', secondary: '#FFFFFF', type: 'plain' },
  'lecce': { primary: '#FDD116', secondary: '#E63946', type: 'plain' },
  'napoli': { primary: '#0093D0', secondary: '#FFFFFF', type: 'plain' },
  'parma': { primary: '#FFFFFF', secondary: '#000000', type: 'plain' },
  'pisa': { primary: '#000000', secondary: '#E32221', type: 'plain' },
  'sassuolo': { primary: '#00A651', secondary: '#000000', type: 'plain' },
  'torino': { primary: '#8B1538', secondary: '#FFFFFF', type: 'plain' },
  'udinese': { primary: '#000000', secondary: '#FFFFFF', type: 'stripes' },
  'verona': { primary: '#1E3A8A', secondary: '#FFD700', type: 'plain' },
  'hellas verona': { primary: '#1E3A8A', secondary: '#FFD700', type: 'plain' },

  // ---------- German Bundesliga (18 Teams) ----------
  '1. fc heidenheim': { primary: '#1E3A5F', secondary: '#FFFFFF', type: 'plain' },
  '1. fc koln': { primary: '#FFFFFF', secondary: '#E32221', type: 'plain' },
  'fc koln': { primary: '#FFFFFF', secondary: '#E32221', type: 'plain' },
  '1899 hoffenheim': { primary: '#1E3A5F', secondary: '#FFFFFF', type: 'plain' },
  'bayer leverkusen': { primary: '#E32221', secondary: '#000000', type: 'plain' },
  'bayern munchen': { primary: '#DC052D', secondary: '#FFFFFF', type: 'plain' },
  'borussia dortmund': { primary: '#FDE100', secondary: '#000000', type: 'stripes' },
  'borussia monchengladbach': { primary: '#000000', secondary: '#FFFFFF', type: 'stripes' },
  'eintracht frankfurt': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },
  'fc augsburg': { primary: '#006B3F', secondary: '#FFFFFF', type: 'plain' },
  'fc st. pauli': { primary: '#3F2A1D', secondary: '#FFFFFF', type: 'plain' },
  'fsv mainz 05': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },
  'hamburger sv': { primary: '#FFFFFF', secondary: '#003399', type: 'plain' },
  'rb leipzig': { primary: '#E32219', secondary: '#FFFFFF', type: 'plain' },
  'sc freiburg': { primary: '#000000', secondary: '#E32221', type: 'plain' },
  'union berlin': { primary: '#EB1923', secondary: '#FFFFFF', type: 'plain' },
  'vfb stuttgart': { primary: '#FFFFFF', secondary: '#E32221', type: 'plain' },
  'vfl wolfsburg': { primary: '#009639', secondary: '#FFFFFF', type: 'plain' },
  'werder bremen': { primary: '#1D9053', secondary: '#FFFFFF', type: 'plain' },

  // ---------- French Ligue 1 (18 Teams) ----------
  'angers': { primary: '#000000', secondary: '#FFFFFF', type: 'plain' },
  'auxerre': { primary: '#003399', secondary: '#FFFFFF', type: 'stripes' },
  'le havre': { primary: '#0093D0', secondary: '#FFFFFF', type: 'plain' },
  'lens': { primary: '#FDE100', secondary: '#E32221', type: 'plain' },
  'lille': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },
  'lorient': { primary: '#E32221', secondary: '#F9D616', type: 'plain' },
  'lyon': { primary: '#00519C', secondary: '#FFFFFF', type: 'plain' },
  'marseille': { primary: '#FFFFFF', secondary: '#6CB4EE', type: 'plain' },
  'metz': { primary: '#8B1538', secondary: '#FFFFFF', type: 'plain' },
  'monaco': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },
  'nantes': { primary: '#FDE100', secondary: '#009639', type: 'plain' },
  'nice': { primary: '#E32221', secondary: '#000000', type: 'stripes' },
  'paris fc': { primary: '#002654', secondary: '#FFFFFF', type: 'plain' },
  'paris saint germain': { primary: '#004170', secondary: '#DA291C', type: 'plain' },
  'reims': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },
  'rennes': { primary: '#E32221', secondary: '#000000', type: 'plain' },
  'stade brestois 29': { primary: '#FFFFFF', secondary: '#E32221', type: 'plain' },
  'strasbourg': { primary: '#003399', secondary: '#FFFFFF', type: 'plain' },

  // ---------- Portuguese Primeira Liga (18 Teams) ----------
  'alverca': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'arouca': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },
  'avs': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'benfica': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },
  'boavista': { primary: '#000000', secondary: '#F9D616', type: 'stripes' },
  'casa pia': { primary: '#F9D616', secondary: '#000000', type: 'stripes' },
  'estoril': { primary: '#F9D616', secondary: '#000000', type: 'plain' },
  'estrela': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'famalicao': { primary: '#FFFFFF', secondary: '#E32221', type: 'stripes' },
  'farense': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'fc porto': { primary: '#005CA5', secondary: '#FFFFFF', type: 'stripes' },
  'gil vicente': { primary: '#E32221', secondary: '#000000', type: 'stripes' },
  'guimaraes': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'moreirense': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'nacional': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'rio ave': { primary: '#009639', secondary: '#FFFFFF', type: 'plain' },
  'santa clara': { primary: '#003399', secondary: '#FFFFFF', type: 'plain' },
  'sc braga': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },
  'vitoria': { primary: '#FFFFFF', secondary: '#000000', type: 'plain' },

  // ---------- Dutch Eredivisie (18 Teams) ----------
  'ajax': { primary: '#FFFFFF', secondary: '#E31937', type: 'plain' },
  'almere city fc': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },
  'az alkmaar': { primary: '#E32221', secondary: '#FFFFFF', type: 'stripes' },
  'de graafschap': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'excelsior': { primary: '#E32221', secondary: '#000000', type: 'stripes' },
  'feyenoord': { primary: '#E32221', secondary: '#FFFFFF', type: 'vertical-split' },
  'fortuna sittard': { primary: '#000000', secondary: '#F9D616', type: 'stripes' },
  'go ahead eagles': { primary: '#F9D616', secondary: '#000000', type: 'stripes' },
  'groningen': { primary: '#009639', secondary: '#FFFFFF', type: 'plain' },
  'heerenveen': { primary: '#003399', secondary: '#FFFFFF', type: 'stripes' },
  'heracles': { primary: '#F9D616', secondary: '#000000', type: 'plain' },
  'nac breda': { primary: '#F9D616', secondary: '#000000', type: 'plain' },
  'nec nijmegen': { primary: '#009639', secondary: '#E32221', type: 'plain' },
  'pec zwolle': { primary: '#009639', secondary: '#FFFFFF', type: 'stripes' },
  'psv eindhoven': { primary: '#E32221', secondary: '#FFFFFF', type: 'stripes' },
  'sparta rotterdam': { primary: '#FFFFFF', secondary: '#E32221', type: 'stripes' },
  'waalwijk': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'willem ii': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },

  // ---------- Turkish Süper Lig (19 Teams) ----------
  'fenerbahce': { primary: '#F9D616', secondary: '#003399', type: 'stripes' },
  'istanbul basaksehir': { primary: '#F37321', secondary: '#003399', type: 'plain' },
  'trabzonspor': { primary: '#7A263B', secondary: '#6CB4EE', type: 'stripes' },
  'rizespor': { primary: '#009639', secondary: '#FFFFFF', type: 'stripes' },
  'goztepe': { primary: '#F9D616', secondary: '#C8102E', type: 'stripes' },
  'adana demirspor': { primary: '#003399', secondary: '#FFFFFF', type: 'plain' },
  'alanyaspor': { primary: '#E32221', secondary: '#FFFFFF', type: 'stripes' },
  'antalyaspor': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },
  'bb bodrumspor': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'besiktas': { primary: '#000000', secondary: '#FFFFFF', type: 'stripes' },
  'galatasaray': { primary: '#F9D616', secondary: '#A00000', type: 'vertical-split' },
  'gaziantep fk': { primary: '#F37321', secondary: '#000000', type: 'plain' },
  'hatayspor': { primary: '#E32221', secondary: '#000000', type: 'stripes' },
  'kasimpasa': { primary: '#009639', secondary: '#F9D616', type: 'plain' },
  'kayserispor': { primary: '#E32221', secondary: '#F9D616', type: 'plain' },
  'kocaelispor': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'konyaspor': { primary: '#009639', secondary: '#FFFFFF', type: 'plain' },
  'samsunspor': { primary: '#E32221', secondary: '#000000', type: 'plain' },
  'sivasspor': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },
  'eyupspor': { primary: '#5B21B6', secondary: '#FFFFFF', type: 'plain' },
  'fatih karagumruk': { primary: '#E32221', secondary: '#FFFFFF', type: 'stripes' },
  'genclerbirligi s.k.': { primary: '#E32221', secondary: '#000000', type: 'stripes' },

  // ---------- Belgian Pro League (16 Teams) ----------
  'anderlecht': { primary: '#51267D', secondary: '#FFFFFF', type: 'plain' },
  'antwerp': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },
  'beerschot': { primary: '#800080', secondary: '#FFFFFF', type: 'plain' },
  'cercle brugge': { primary: '#009639', secondary: '#FFFFFF', type: 'stripes' },
  'charleroi': { primary: '#E32221', secondary: '#000000', type: 'plain' },
  'club brugge kv': { primary: '#003399', secondary: '#F9D616', type: 'plain' },
  'eupen': { primary: '#000000', secondary: '#FFFFFF', type: 'plain' },
  'genk': { primary: '#0055A4', secondary: '#FFFFFF', type: 'plain' },
  'gent': { primary: '#003399', secondary: '#FFFFFF', type: 'plain' },
  'kortrijk': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },
  'kv mechelen': { primary: '#F9D616', secondary: '#E32221', type: 'stripes' },
  'oh leuven': { primary: '#FFFFFF', secondary: '#000000', type: 'plain' },
  'standard liege': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },
  'st truiden': { primary: '#F9D616', secondary: '#003399', type: 'stripes' },
  'union saint gilloise': { primary: '#F9D616', secondary: '#003399', type: 'stripes' },
  'westerlo': { primary: '#F9D616', secondary: '#000000', type: 'stripes' },

  // ---------- Austrian Bundesliga (12 Teams) ----------
  'austria lustenau': { primary: '#009639', secondary: '#FFFFFF', type: 'plain' },
  'austria vienna': { primary: '#6B2D5C', secondary: '#FFFFFF', type: 'plain' },
  'blau weiss linz': { primary: '#0055A4', secondary: '#FFFFFF', type: 'plain' },
  'grazer ak': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },
  'hartberg': { primary: '#0055A4', secondary: '#FFFFFF', type: 'plain' },
  'lask linz': { primary: '#000000', secondary: '#FFFFFF', type: 'stripes' },
  'rapid vienna': { primary: '#009639', secondary: '#FFFFFF', type: 'hoops' },
  'red bull salzburg': { primary: '#FFFFFF', secondary: '#E32221', type: 'plain' },
  'rheindorf altach': { primary: '#000000', secondary: '#FFFFFF', type: 'plain' },
  'sturm graz': { primary: '#000000', secondary: '#FFFFFF', type: 'stripes' },
  'wolfsberger ac': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'wsg tirol': { primary: '#009639', secondary: '#FFFFFF', type: 'plain' },

  // ---------- Israeli Premier League (Ligat Ha'Al) 2025/26 ----------
  'ashdod': { primary: '#E32221', secondary: '#F9D616', type: 'plain' },
  'beitar jerusalem': { primary: '#FDEF42', secondary: '#000000', type: 'plain' },
  'bnei sakhnin': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },
  'hapoel beer sheva': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },
  'hapoel haifa': { primary: '#E32221', secondary: '#000000', type: 'plain' },
  'hapoel petah tikva': { primary: '#0066B3', secondary: '#FFFFFF', type: 'plain' },
  'hapoel tel aviv': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },
  'ironi kiryat shmona': { primary: '#005BAB', secondary: '#FFFFFF', type: 'plain' },
  'ironi tiberias': { primary: '#005BAB', secondary: '#FDEF42', type: 'plain' },
  'maccabi bnei raina': { primary: '#FDEF42', secondary: '#005BAB', type: 'plain' },
  'maccabi haifa': { primary: '#00914C', secondary: '#FFFFFF', type: 'plain' },
  'maccabi netanya': { primary: '#FDEF42', secondary: '#000000', type: 'plain' },
  'maccabi tel aviv': { primary: '#FDEF42', secondary: '#005BAB', type: 'stripes' },

  // ---------- Rest (A–Z) ----------
  '1. fc kaiserslautern': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },
  '1. fc magdeburg': { primary: '#0066B3', secondary: '#FFFFFF', type: 'plain' },
  '1. fc nurnberg': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },
  '1874 northwich': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'abbeville': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'abbey hey': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'abbey hulton': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'abbey rangers': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'aberdeen': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },
  'abingdon united': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'accrington st': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },
  'ad ceuta fc': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'ado den haag': { primary: '#F9D616', secondary: '#009639', type: 'plain' },
  'aek athens fc': { primary: '#FFE600', secondary: '#000000', type: 'plain' },
  'aek larnaca': { primary: '#FFE600', secondary: '#0066B3', type: 'plain' },
  'afad': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'afc croydon athletic': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'afc dunstable': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'afc emley': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'afc fylde': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },
  'afc hornchurch': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'afc kempston rovers': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'afc liverpool': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'afc mansfield': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'afc portchester': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'afc rushden & diamonds': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'afc stoneham': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'afc sudbury': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'afc telford united': { primary: '#E32221', secondary: '#000000', type: 'plain' },
  'afc totton': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'afc whyteleafe': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'afc wimbledon': { primary: '#00205B', secondary: '#F9D616', type: 'plain' },
  'afc wulfrunians': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'agde': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'aik stockholm': { primary: '#000000', secondary: '#FFE600', type: 'plain' },
  'aktobe': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'al ahli jeddah': { primary: '#006B3F', secondary: '#1E3A8A', type: 'plain' },
  'al-ahli jeddah': { primary: '#006B3F', secondary: '#1E3A8A', type: 'plain' },
  'al ettifaq': { primary: '#009639', secondary: '#FFFFFF', type: 'plain' },
  'al-ettifaq': { primary: '#009639', secondary: '#FFFFFF', type: 'plain' },
  'al fateh': { primary: '#009639', secondary: '#FFFFFF', type: 'plain' },
  'al-fateh': { primary: '#009639', secondary: '#FFFFFF', type: 'plain' },
  'al fayha': { primary: '#7C3AED', secondary: '#FFFFFF', type: 'plain' },
  'al-fayha': { primary: '#7C3AED', secondary: '#FFFFFF', type: 'plain' },
  'al hazm': { primary: '#F9D616', secondary: '#000000', type: 'plain' },
  'al-hazm': { primary: '#F9D616', secondary: '#000000', type: 'plain' },
  'al hilal saudi fc': { primary: '#003B7A', secondary: '#FFFFFF', type: 'plain' },
  'al-hilal saudi fc': { primary: '#003B7A', secondary: '#FFFFFF', type: 'plain' },
  'al ittihad fc': { primary: '#000000', secondary: '#F9D616', type: 'plain' },
  'al-ittihad fc': { primary: '#000000', secondary: '#F9D616', type: 'plain' },
  'al khaleej saihat': { primary: '#009639', secondary: '#000000', type: 'plain' },
  'al kholood': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },
  'al najma': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'al nassr': { primary: '#F9D616', secondary: '#003FA6', type: 'plain' },
  'al-nassr': { primary: '#F9D616', secondary: '#003FA6', type: 'plain' },
  'al okhdood': { primary: '#E32221', secondary: '#000000', type: 'plain' },
  'al qadisiyah fc': { primary: '#003B7A', secondary: '#FFFFFF', type: 'plain' },
  'al-qadisiyah fc': { primary: '#003B7A', secondary: '#FFFFFF', type: 'plain' },
  'al riyadh': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },
  'al shabab': { primary: '#000000', secondary: '#FFFFFF', type: 'plain' },
  'al taawon': { primary: '#FACC15', secondary: '#1E3A8A', type: 'plain' },
  'albacete': { primary: '#FFFFFF', secondary: '#000000', type: 'plain' },
  'alberite': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'albion sports': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'aldershot town': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },
  'aldosivi': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'alfreton town': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },
  'almeria': { primary: '#E63946', secondary: '#000000', type: 'stripes' },
  'alton town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'altrincham': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },
  'alvechurch': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'amersham town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'amiens': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },
  'ancienne chateau gontier': { primary: '#0066B3', secondary: '#FFFFFF', type: 'plain' },
  'andover new street': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'andrezieux': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },
  'angouleme': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },
  'annecy': { primary: '#0066B3', secondary: '#FFFFFF', type: 'plain' },
  'anstey nomads': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'antequera': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'antoniano': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'apm metz': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'apoel nicosia': { primary: '#FFE600', secondary: '#0066B3', type: 'plain' },
  'araba': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'ararat armenia': { primary: '#D52B1E', secondary: '#FFFFFF', type: 'plain' },
  'araz': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'arda kardzhali': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'ardley united': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'arenas getxo': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'argentinos jrs': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },
  'aris': { primary: '#F9D616', secondary: '#000000', type: 'plain' },
  'aris thessalonikis': { primary: '#F9D616', secondary: '#000000', type: 'plain' },
  'arlesey town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'arminia bielefeld': { primary: '#000000', secondary: '#FFFFFF', type: 'plain' },
  'ascot united': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'asec mimosas': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'ashby ivanhoe': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'ashford town (middlesex)': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'ashford united': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'ashington afc': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'ashton united': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },
  'asptt dijon': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'ass still mutzig': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'atherstone town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'atherton collieries': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'atherton laburnum rovers': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'athletic newham': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'atlanta united fc': { primary: '#A6192E', secondary: '#000000', type: 'plain' },
  'atlas': { primary: '#E32221', secondary: '#000000', type: 'stripes' },
  'atlas delmenhorst': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'atlas paillade': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'atletic lleida': { primary: '#CB3524', secondary: '#FFFFFF', type: 'plain' },
  'atletico astorga': { primary: '#CB3524', secondary: '#FFFFFF', type: 'plain' },
  'atletico baleares': { primary: '#CB3524', secondary: '#FFFFFF', type: 'plain' },
  'atletico calatayud': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'atletico goianiense': { primary: '#E32221', secondary: '#000000', type: 'plain' },
  'atletico melilla': { primary: '#CB3524', secondary: '#FFFFFF', type: 'plain' },
  'atletico mg': { primary: '#E32221', secondary: '#000000', type: 'plain' },
  'atletico paranaense': { primary: '#E32221', secondary: '#000000', type: 'stripes' },
  'atletico san luis': { primary: '#CB3524', secondary: '#FFFFFF', type: 'plain' },
  'atletico tordesillas': { primary: '#CB3524', secondary: '#FFFFFF', type: 'plain' },
  'atletico tucuman': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },
  'aubagne': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'aubervilliers': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'auby': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'auch': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'auda': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'audace cerignola': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'austin': { primary: '#006B3F', secondary: '#000000', type: 'plain' },
  'aveley': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },
  'avellino': { primary: '#15803D', secondary: '#FFFFFF', type: 'plain' },
  'avenir sportif de gouzon': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'avion': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'avranches': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },
  'avro': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'aylesbury united': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'aylesbury vale dynamos': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'aylestone park': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'azuaga': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'b36 torshavn': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'badshot lea': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'baffins milton rovers': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'bahia': { primary: '#009639', secondary: '#FFFFFF', type: 'plain' },
  'bahlinger sc': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'bala town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'baldock town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'balham': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'ballkani': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'bamber bridge': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'banbury united': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'banck ostrava': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'bandrele': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'banfield': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'banga': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'bari': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'barking': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'barnet': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'barnoldswick town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'barnsley': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'barnstaple town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'barracas central': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'barrow': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'barton rovers': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'barton town old boys': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'barwell': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'basaksehir': { primary: '#000000', secondary: '#F9D616', type: 'plain' },
  'basford united': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'bashley': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'basildon united': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'basingstoke town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'bastia': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'bath city': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'bayeux': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'bayonne': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'beaconsfield town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'bearsted': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'beaucouze': { primary: '#0066B3', secondary: '#FFFFFF', type: 'plain' },
  'beauvais': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'beckenham town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'bedfont sports': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'bedford town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'bedworth united': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'beitar haifa': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'beitar ramat gan': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'belgrano cordoba': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'belper town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'belper united': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'bemerton heath harleq.': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'benfleet': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'berkhamsted': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'betis valladolid': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'beverley town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'bexhill united': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'bfc daugavpils': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'bfc dynamo': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'biars bretenoux': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },
  'bideford': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'biesheim': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'biggleswade': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'biggleswade town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'biggleswade united': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'billericay town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'binfield': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'birkirkara': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'birmingham': { primary: '#003399', secondary: '#FFFFFF', type: 'plain' },
  'birtley town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'bishop auckland': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'bk hacken': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'blackburn': { primary: '#003399', secondary: '#FFFFFF', type: 'plain' },
  'blackpool': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'blackstones': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'blagnac': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'blois': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'blyth spartans': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'blyth town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'bnei hagolan vehagalil': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'bnei jaffa ortodoxim': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'bnei yehuda': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'bo bon encontre': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'boca juniors': { primary: '#003399', secondary: '#F9D616', type: 'plain' },
  'bodo/glimt': { primary: '#FFDD00', secondary: '#000000', type: 'plain' },
  'bognor regis town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'bogny': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'boldmere st. michaels': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'bolton': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'bondues': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'bootle': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'borac banja luka': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'bordeaux': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'boreham wood': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'boro rangers': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'borussia mbnchengladbach': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'boston town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'boston united': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'botafogo': { primary: '#000000', secondary: '#FFFFFF', type: 'stripes' },
  'botev plovdiv': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'bottesford town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'bouak': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'boulogne': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'bourg en bresse 01': { primary: '#003399', secondary: '#FFFFFF', type: 'plain' },
  'bourne town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'bournemouth fc': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'bowers & pitsea': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'brackley town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'bracknell town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'bradford': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'bradford (park avenue)': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'bradford town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'braintree': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'brann': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'brantham athletic': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'bravo': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'breidablik': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'brentwood town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'brescia': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'bridgwater town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'bridlington town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'brighouse town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'brightlingsea regent': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'brislington': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'bristol city': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },
  'bristol manor farm': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'bristol rovers': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'british airways': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'brixham': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'broadbridge heath': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'broadfields united': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'brockenhurst': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'brocton': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'bromley': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'bromsgrove sporting': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'brondby': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'bsc young boys': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'buckhurst hill': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'buckland athletic': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'buducnost podgorica': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'bugbrooke st michaels': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'burgess hill town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'burgos': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'burnham': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'burscough': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'burton albion': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'bury': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'bury town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'buxton': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'bw lohne': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'cacereco': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'cadiz': { primary: '#FDD116', secondary: '#0066B3', type: 'plain' },
  'caen': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'caen ptt': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'caernarfon town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'camberley town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'cambrai': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'cambridge city': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'cambridge united': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'cambuur': { primary: '#F9D616', secondary: '#000000', type: 'stripes' },
  'camon': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'campanario': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'campion': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'canet roussillon': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'cannes': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'canvey island': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'cardiff': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'carlisle': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'carlisle city': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'carlton town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'carnoux': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'carrarese': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'carshalton athletic': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'castelln': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'catania': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'catanzaro': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'caudal': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'cd sant jordi': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'cd yuncos': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'ceara': { primary: '#000000', secondary: '#FFFFFF', type: 'plain' },
  'celje': { primary: '#1B2A54', secondary: '#FFE600', type: 'plain' },
  'celtic': { primary: '#009639', secondary: '#FFFFFF', type: 'hoops' },
  'central cordoba de santiago': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'cesena': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'cf montreal': { primary: '#003399', secondary: '#F9D616', type: 'plain' },
  'cf talavera': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'cfr 1907 cluj': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'chadderton': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'challans': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'chalon': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'chamalicres': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'chambly thelle fc': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'chantilly': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'charlotte': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'charlton': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },
  'charnock richard': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'chasetown': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'chateauroux': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },
  'chatham town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'chauvigny': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'cheadle town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'chelmsford city': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'cheltenham': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'cherno more varna': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'chertsey town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'chesham united': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'cheshunt': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'chester': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'chesterfield': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'chicago fire': { primary: '#E32221', secondary: '#003399', type: 'plain' },
  'chichester city': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'chippenham town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'chipstead': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'chorley': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'christchurch': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'cieza': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'cinderford town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'cirencester town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'cittadella': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'city of liverpool': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'ciudad de lucena': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'clay cross': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'cleethorpes town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'clermont foot': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'clevedon town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'cliftonville fc': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'clitheroe': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'club america': { primary: '#F9D616', secondary: '#003399', type: 'plain' },
  'club queretaro': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'club tijuana': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'cobham': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'cockfosters': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'colchester': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'coleshill town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'colney heath': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'colomiers': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'colorado rapids': { primary: '#862633', secondary: '#96C0E6', type: 'plain' },
  'columbus crew': { primary: '#F9D616', secondary: '#000000', type: 'plain' },
  'concarneau': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'concord rangers': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'congleton town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'consett': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'constcncia': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'corby town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'cordoba': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'corinthian': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'corinthian casuals': { primary: '#7B3F00', secondary: '#FFC0CB', type: 'plain' },
  'corinthians': { primary: '#000000', secondary: '#FFFFFF', type: 'plain' },
  'cornard united': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'corsham town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'corvinul hunedoara': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'cosenza': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'coton green': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'coventry': { primary: '#6CB4EE', secondary: '#FFFFFF', type: 'plain' },
  'coventry sphinx': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'coventry united': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'cowes sports': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'crawley down gatwick': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'crawley green': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'crawley town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'cray valley pm': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'cray wanderers': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'crewe': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'cribbs': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'criciuma': { primary: '#F9D616', secondary: '#000000', type: 'plain' },
  'croix football ic': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'crook town afc': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'crowborough athletic': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'croydon athletic': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'crusaders fc': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'cruz azul': { primary: '#003399', secondary: '#FFFFFF', type: 'plain' },
  'cruzeiro': { primary: '#003399', secondary: '#FFFFFF', type: 'plain' },
  'cska 1948': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'cuiaba': { primary: '#009639', secondary: '#F9D616', type: 'plain' },
  'cultural leonesa': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'curzon ashton': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'dagenham & redbridge': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'damac': { primary: '#F9D616', secondary: '#FFFFFF', type: 'plain' },
  'darlaston town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'darlington 1883': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'dartford': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'daventry town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'dc united': { primary: '#000000', secondary: '#E32221', type: 'plain' },
  'deal town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'decic': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'deeping rangers': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'defensa y justicia': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'den bosch': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'deportivo getxo': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'deportivo la coruna': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'deportivo riestra': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'derby': { primary: '#FFFFFF', secondary: '#000000', type: 'plain' },
  'dereham town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'derry city': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'didcot town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'dieppe': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'dila': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'dimona': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'dinamo batumi': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'dinamo brest': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'dinamo minsk': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'dinamo tbilisi': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'dinamo tirana': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'dinamo zagreb': { primary: '#0D5EAF', secondary: '#FFFFFF', type: 'plain' },
  'dinan lhon': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'djurgardens if': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'dnipro 1': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'dominicaine': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'doncaster': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'dorchester town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'dordrecht': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'dorking wanderers': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'dover': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'downham town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'downton': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'drancy': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'drita': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'droitwich spa': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'dudley town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'dulwich hamlet': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'dunajska streda': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'dundee': { primary: '#003399', secondary: '#FFFFFF', type: 'stripes' },
  'dundee utd': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'dungannon swifts': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'dunkerque': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'dunstable town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'dunston uts': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'dynamo dresden': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'dynamo kyiv': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'easington colliery': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'easington sports': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'east cowes victoria': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'east grinstead town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'eastbourne borough': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'eastbourne town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'eastbourne united': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'eastleigh': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'eastwood community': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'ebbsfleet united': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'ebro': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'eccleshill united': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'ecotay moingt': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'edgware town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'egham town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'egnatia rrogozhinc': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'eibar': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'eintracht braunschweig': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'eintracht norderstedt': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'eldense': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'ely city': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'empoli': { primary: '#003399', secondary: '#FFFFFF', type: 'plain' },
  'energie cottbus': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'enfield 1893': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'enfield town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'epernay': { primary: '#87CEEB', secondary: '#FFFFFF', type: 'plain' },
  'epsom & ewell fc': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'erith & belvedere': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'erith town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'es agboville': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'es cappelle': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'espoir ste luce': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'estac troyes': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'estepona': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'estudiantes l.p.': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'etoile bobigny': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'etoile matoury': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'europa fc': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'euxton villa': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'eveil': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'eversley & cal': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'evesham united': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'exeter city': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'exmouth': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'extremadura 1924': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'eynesbury rovers': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'eyupspor': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'f91 dudelange': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'fairford town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'fakenham town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'falkirk': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'falmouth town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'fareham town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'farnborough': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'farnham town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'fatih karagumruk': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'faversham sf': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'faversham town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'fc 08 homburg': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'fc 2 rives 82': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'fc andorra': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'fc astana': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'fc basel 1893': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'fc beer sheva': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'fc cartagena': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'fc cincinnati': { primary: '#003087', secondary: '#FC4C02', type: 'plain' },
  'fc copenhagen': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'fc dallas': { primary: '#003399', secondary: '#E32221', type: 'plain' },
  'fc differdange 03': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'fc gutersloh': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'fc halifax town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'fc isloch minsk r.': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'fc jerusalem': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'fc juarez': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'fc levadia tallinn': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'fc lugano': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'fc midtjylland': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'fc noah': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'fc saarbrucken': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'fc santa coloma': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'fc schalke 04': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'fc schweinfurt 05': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'fc st. gallen': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'fc urartu': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'fc vaduz': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'fc viktoria kbln': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'fc volendam': { primary: '#F37321', secondary: '#FFFFFF', type: 'plain' },
  'fc zurich': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'fcsb': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'fehrvcr fc': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'feignies aulnoye': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },
  'felixstowe & walton utd': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'fenerbahce': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'ferencvarosi tc': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'feurs': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'fisher': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'fk crvena zvezda': { primary: '#E4002B', secondary: '#FFFFFF', type: 'stripes' },
  'fk kosice': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },
  'fk liepaja': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'fk partizan': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'fk rabotnicki': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'fk sarajevo': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'fk tobol kostanay': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'fk zalgiris vilnius': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'flackwell heath': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'flamengo': { primary: '#E32221', secondary: '#000000', type: 'plain' },
  'fleet town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'fleetwood town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'fleury 91': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'flora tallinn': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'floriana': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'fluminense': { primary: '#009639', secondary: '#E32221', type: 'stripes' },
  'folkestone invicta': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'forbach': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'forest green': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'forest row': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'fortaleza ec': { primary: '#003B7A', secondary: '#E32221', type: 'plain' },
  'fortuna dusseldorf': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'france aizenay': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'fredrikstad': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'frenford': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'freyming': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'frickley athletic': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'frome town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'frosinone': { primary: '#FDD116', secondary: '#003366', type: 'stripes' },
  'furiani agliani': { primary: '#003399', secondary: '#FFFFFF', type: 'plain' },
  'fv engers 07': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'gainsborough trinity': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'gap connah s quay fc': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'garforth town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'gateshead': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'gazelec fc ajaccio': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'gbztepe': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'genclerbirlici s.k.': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'gillingham': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'gimnasia l.p.': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'gimnastic': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'glebe': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'glossop north end': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'gloucester city': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'gobelins': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'godmanchester rovers': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'godoy cruz': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'golcar united': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'gorleston': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'gosier': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'gosport borough': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'granada cf': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'grantham town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'granville': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'grasse': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'grays athletic': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'great wakering rovers': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'great yarmouth town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'gremio': { primary: '#003399', secondary: '#FFFFFF', type: 'stripes' },
  'grenoble': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'gresley': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'grimsby': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'grimsby borough': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'gsa tomblaine': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'guadalajara': { primary: '#E32221', secondary: '#FFFFFF', type: 'stripes' },
  'guadalajara chivas': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'guernsey': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'guildford city': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'guingamp': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'guisborough town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'guiseley afc': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'gyori eto fc': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'hackney wick': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'hadleigh united': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'hadley': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'halesowen town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'hallam': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'hallen': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'hallescher fc': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'halstead town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'hamble club': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'hammarby ff': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'hampton & richmond': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'hamrun spartans': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'hamworthy recreation': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'handsworth parramore': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'hanley town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'hannover 96': { primary: '#000000', secondary: '#E32221', type: 'plain' },
  'hansa rostock': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'hanwell town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'hanworth villa': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'hapoel acre': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'hapoel afula': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'hapoel hadera': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'hapoel ironi karmiel': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'hapoel katamon': { primary: '#009639', secondary: '#FFFFFF', type: 'plain' },
  'hapoel kfar saba': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'hapoel kfar shalem': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'hapoel migdal haemek': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'hapoel nazareth illit': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'hapoel ramat gan': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'hapoel rishon lezion': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'hapoel sderot': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'hapoel tirat hacarmel': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'hapoel umm al fahm': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },
  'hapoel yarka': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'harborough town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'harefield united': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'haringey borough': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'harleston town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'harlow town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'harpenden town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'harrogate town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'harrow borough': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'harrowby united': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'hartlepool': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'hartley wintney': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'hartpury university': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'harwich & parkeston': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'hashtag united': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'hassocks': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'hastings united': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'hauts lyonnais': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'havant & wville': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'haverfordwest county afc': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'haverhill rovers': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'hayes & yeading united': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'haywards heath town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'hb': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'heacham': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'heanor town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'heart of midlothian': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'heaton stannington': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'hebburn town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'hednesford town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'hegelmann litauen': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'helston athletic': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'hemel hempstead town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'hemelingen': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'hendon': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'hereford': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'hereford pegasus': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'herne bay': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'hertford town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'hertha bsc': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'heybridge swifts': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'hibernian': { primary: '#009639', secondary: '#FFFFFF', type: 'plain' },
  'hibernians': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'highgate united': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'highworth town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'hilltop': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'hinckley afc': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'histon': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'hitchin town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'hjk helsinki': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'hnk hajduk split': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'hnk rijeka': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'hollands & blair': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'holmesdale': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'holon yermiyahu': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'holstein kiel': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'holyport': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'horbury town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'horden cw': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'horley town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'horndean': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'horsham': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'horsham ymca': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'houston dynamo': { primary: '#E32221', secondary: '#003399', type: 'plain' },
  'hucknall town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'huddersfield': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'huesca': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'hull city': { primary: '#F97316', secondary: '#000000', type: 'stripes' },
  'hullbridge sports': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'hungerford town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'huracan': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'hyde united': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'hythe & dibden': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'hythe town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'ibiza': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'if elfsborg': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'ihud bnei majd al krum': { primary: '#F9D616', secondary: '#003B7A', type: 'plain' },
  'ilford': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'ilkeston town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'illertissen': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'ilves': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'independ. rivadavia': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'independiente': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },
  'instituto cordoba': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'inter de valdemoro': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'inter miami': { primary: '#E32221', secondary: '#F9D616', type: 'plain' },
  'internacional': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },
  'ipswich': { primary: '#003399', secondary: '#FFFFFF', type: 'plain' },
  'ipswich wanderers': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'irlam': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'ironi or yehuda': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'isca': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'istres': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'ivry': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'ivybridge town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'jagiellonia': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'jersey bulls': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'juve stabia': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'juventud torremolinos': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'juventude': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'ka akureyri': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'kafr qasim': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'kairat almaty': { primary: '#FFD100', secondary: '#000000', type: 'stripes' },
  'kalju nomme': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'karlsruher sc': { primary: '#0066B3', secondary: '#FFFFFF', type: 'plain' },
  'kauno zalgiris': { primary: '#009639', secondary: '#FFFFFF', type: 'plain' },
  'kendal town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'kennington': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'kettering town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'kfar kama': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'kfar saba 1928': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'ki klaksvik': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'kidderminster harriers': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'kidlington': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'kidsgrove athletic': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'kilmarnock': { primary: '#003399', secondary: '#FFFFFF', type: 'stripes' },
  'kimberley miners welfare': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'kings langley': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'kingstonian': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'kirkley & pakefield': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'kiryat haim': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'kiryat yam sc': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'knaphill': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'knaresborough town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'koper': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'korhogo': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'kryvbas kr': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'kups': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'la fiorita': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'la unin atltico': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'lakenheath': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'lancaster city': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'lancing': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'langreo': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'lanus': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'lapalisse': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'larkfield & new hythe': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'larkhall athletic': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'larne': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'las palmas': { primary: '#FEDD00', secondary: '#003366', type: 'vertical-split' },
  'lausanne': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'laval': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'lavaur': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'laverstock & ford': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'le cellier mauves': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'le geldar': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'le mans': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'le poinconnet': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'le puy foot': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'leamington': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'leatherhead': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'lech poznan': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'leek town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'leganes': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'legia warszawa': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'leicester': { primary: '#003090', secondary: '#FFFFFF', type: 'plain' },
  'leicester nirvana': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'leighton town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'leiston': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'leon': { primary: '#009639', secondary: '#FFFFFF', type: 'plain' },
  'les bleuets': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'les herbiers': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'leverstock green': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'levski sofia': { primary: '#0057B8', secondary: '#FFFFFF', type: 'plain' },
  'lewes': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'leyton orient': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'lichfield city': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'lincoln': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'lincoln red imps fc': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'lincoln united': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'linfield': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'lingfield': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'litherland remyca': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'little common': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'little oakley': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'littlehampton town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'liversedge': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'livingston': { primary: '#F9D616', secondary: '#000000', type: 'plain' },
  'llapi': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'lokomotive leipzig': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'london lions': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'long eaton united': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'longlevens': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'longridge town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'longuenesse': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'loon plage': { primary: '#003399', secondary: '#FFFFFF', type: 'plain' },
  'lorca deportiva': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'los angeles fc': { primary: '#000000', secondary: '#E32221', type: 'plain' },
  'los angeles galaxy': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'los garres': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'loughborough university': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'lourdes': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'lower breck': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'lowestoft town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'ludogorets': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'luton': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'lutterworth town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'lydney town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'lye town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'lyon duchere': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'maccabi ahi nazareth': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'maccabi ashdod': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'maccabi b. jadeidi makr': { primary: '#F9D616', secondary: '#003B7A', type: 'plain' },
  'maccabi hasharon netanya': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'maccabi herzliya': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'maccabi k. ata bialik': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'maccabi kabilio jaffa': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'maccabi kiryat gat': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'maccabi petah tikva': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'maccabi yavne': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'macclesfield': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'magenta': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'magpies': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'maidenhead': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'maidstone utd': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'malaga': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'maldon & tiptree': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'malisheva': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'malmo ff': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'malvern town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'mangotsfield united': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'manises': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'mansfield town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'mantova': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'maracena': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'march town united': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'margate': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'maribor': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'marine': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'marlow': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'marmande': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'marsaxlokk': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'marske united': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'matlock town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'mauguio carnon': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'mazatlcn': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'mazeres': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'melksham town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'melton town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'merstham': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'merthyr town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'metropolitan police': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'mickleover sports': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'middlesbrough': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },
  'midhurst & easebourne': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'mildenhall town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'millbrook fc': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'millwall': { primary: '#FFFFFF', secondary: '#000000', type: 'plain' },
  'milsami orhei': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'milton keynes dons': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'milton keynes irish': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'milton united': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'minnesota united fc': { primary: '#1C1C1C', secondary: '#CCCCCC', type: 'plain' },
  'mirandes': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'mirassol': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'mjep cormontreuil': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'mlada boleslav': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'modena': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'molde': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'moneyfields': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'montceau': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'monterrey': { primary: '#FFFFFF', secondary: '#003399', type: 'stripes' },
  'montmorillon': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'montpellier': { primary: '#003399', secondary: '#FFFFFF', type: 'plain' },
  'montreal la cluse': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },
  'montreuil': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'monza': { primary: '#C8102E', secondary: '#FFFFFF', type: 'stripes' },
  'morecambe': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'mornar': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'morpeth town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'mossley': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'motherwell': { primary: '#F9D616', secondary: '#8B1538', type: 'plain' },
  'moulien': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'moulton': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'mouna': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'mousehole': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'mrida ad': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'mulbarton wanderers': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'mulhouse': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'mutilvera': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'nailsea & tickenham': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'nancy': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'nantwich town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'nashville sc': { primary: '#F9D616', secondary: '#003399', type: 'plain' },
  'navalcarnero': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'ncxara': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'necaxa': { primary: '#E32221', secondary: '#FFFFFF', type: 'stripes' },
  'needham market': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'neman': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'neom': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'new england revolution': { primary: '#012169', secondary: '#E32221', type: 'plain' },
  'new milton town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'new york city fc': { primary: '#003399', secondary: '#FFFFFF', type: 'plain' },
  'new york red bulls': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },
  'newark flowserve': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'newark town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'newcastle benfield': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'newcastle blue star': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'newcastle town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'newells old boys': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'newhaven': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'newmarket': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'newmarket town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'newport county': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'newport pagnell town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'newquay': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'newton aycliffe': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'nk osijek': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'nk varazdin': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'nord lozcre': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'north ferriby': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'north greenford united': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'north leigh': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'north shields': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'northallerton town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'northampton': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'northampton on chenecks': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'northwich victoria': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'northwood': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'norwich': { primary: '#F9D616', secondary: '#009639', type: 'plain' },
  'notts county': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'novi pazar': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'nsi runavik': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'numancia': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'nuneaton town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'oadby town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'ocpam': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'oissel': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'oldham': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'oldland abbotonians': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'oleksandria': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'olimpija ljubljana': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'olympiakos piraeus': { primary: '#E30613', secondary: '#FFFFFF', type: 'stripes' },
  'olympique de villefontaine': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'olympique marcquois': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'omonia nicosia': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'ordabasy': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'orihuela': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'orlando city sc': { primary: '#612D91', secondary: '#F9D616', type: 'plain' },
  'orleans': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'orvault sf': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'os aire sur la lys': { primary: '#003399', secondary: '#FFFFFF', type: 'plain' },
  'ossett united': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'ourense cf': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'oxford city': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'oxford united': { primary: '#F9D616', secondary: '#003399', type: 'plain' },
  'pachuca': { primary: '#003399', secondary: '#FFFFFF', type: 'plain' },
  'padiham': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'padova': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'pafos': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'pagham': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'paide': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'paks': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'palermo': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'palma del rio': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'palmeiras': { primary: '#009639', secondary: '#FFFFFF', type: 'plain' },
  'panathinaikos': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'panevezys': { primary: '#0066B3', secondary: '#FFFFFF', type: 'plain' },
  'paok': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'parkgate': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'partizani': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'pau': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'paulton rovers': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'peacehaven & telscombe': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'pen hir': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'penistone church': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'penrith afc': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'penybont': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'pershore town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'pescara': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'peterborough': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'peterborough sports': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'petersfield town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'petrocub': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'philadelphia union': { primary: '#003399', secondary: '#F9D616', type: 'plain' },
  'phoenix sports': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'pickering town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'pierrelatte': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'pilkington': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'pirae': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'pirmasens': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'platense': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'pldran': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'plouvorn': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'plymouth': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'plymouth parkway': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'plzen': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'poblense': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'poitiers': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'polessya': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'ponferradina': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'pontarlier': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'pontefract collieries': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'pontevedra': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'pontivy gsi': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'poole town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'port vale': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'portishead town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'portland timbers': { primary: '#009639', secondary: '#F9D616', type: 'plain' },
  'portland united': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'porto portugais amiens': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'portsmouth': { primary: '#003399', secondary: '#FFFFFF', type: 'plain' },
  'portugalete': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'potters bar town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'potton united': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'premontre': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'prescot cables': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'preston': { primary: '#FFFFFF', secondary: '#E32221', type: 'plain' },
  'prestwich heys': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'preucen munster': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'prigny': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'prishtina': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'progres niederkorn': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'puebla': { primary: '#003399', secondary: '#FFFFFF', type: 'stripes' },
  'puente genil': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'puerto de vega': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'punjab united': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'puskas academy': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'pyunik yerevan': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'qarabag': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'qpr': { primary: '#003399', secondary: '#FFFFFF', type: 'hoops' },
  'quetigny': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'quevilly': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'quintanar del rey': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'quorn': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'racing besancon': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'racing club': { primary: '#003399', secondary: '#FFFFFF', type: 'stripes' },
  'racing club warwick': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'racing colombes 92': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'racing fc union luxembourg': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'racing ferrol': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'racing santander': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'radcliffe': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'radnicki 1923': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'rakw czcstochowa': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'ramsbottom united': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'ramsgate': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'rangers': { primary: '#003399', secondary: '#FFFFFF', type: 'plain' },
  'rayners lane': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'raynes park vale': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'rayo majadahonda': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'rb bragantino': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },
  'rcgas fs': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'reading': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'reading city': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'real avils': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'real bedford': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'real cvila': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'real jan': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'real murcia': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'real salt lake': { primary: '#003399', secondary: '#E32221', type: 'plain' },
  'red star fc 93': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'redbridge': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'redcar athletic': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'reddis': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'redditch united': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'redhill': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'reggiana': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'reims sainte anne': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },
  'rhone vallee': { primary: '#003399', secondary: '#FFFFFF', type: 'plain' },
  'riga': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'rimini': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'risborough rangers': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'river plate': { primary: '#FFFFFF', secondary: '#E32221', type: 'plain' },
  'rochdale': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'roda': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'rodez': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'roffey': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'roman glass st george': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'romford': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'romorantin': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'romulus': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'rosario central': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'rosenborg': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'rossington main': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'rot weic essen': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'rotherham': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'royal wootton': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'royston town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'rsd alcalc': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'rsv eintracht': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'rugby borough': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'rugby town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'runcorn linnets': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'rushall olympic': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'rusthall': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'ruzomberok': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },
  'rylands': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'sa beaumont': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'sa mrignac': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'sabadell': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'sabah fa': { primary: '#000000', secondary: '#EC5FA4', type: 'plain' },
  'sabl': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'saburtalo': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'saffron walden town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'saint amand': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },
  'saint brice courcelles': { primary: '#003399', secondary: '#FFFFFF', type: 'plain' },
  'saint colomban locmin': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'saint cyr collonges': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },
  'saint etienne': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'saint malo': { primary: '#003399', secondary: '#FFFFFF', type: 'plain' },
  'saint meziery': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },
  'saint pauloise': { primary: '#003399', secondary: '#F9D616', type: 'plain' },
  'saint renan': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },
  'salernitana': { primary: '#8B1538', secondary: '#F9D616', type: 'plain' },
  'salford city': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'salisbury': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'saltash united': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'sampdoria': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'san diego': { primary: '#003087', secondary: '#F37321', type: 'plain' },
  'san fernando cd': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'san jose earthquakes': { primary: '#003399', secondary: '#F9D616', type: 'plain' },
  'san lorenzo': { primary: '#E32221', secondary: '#FFFFFF', type: 'stripes' },
  'san martin s.j.': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'san pdro': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'sant andreu': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'sant just': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'santos': { primary: '#FFFFFF', secondary: '#000000', type: 'plain' },
  'santos laguna': { primary: '#009639', secondary: '#FFFFFF', type: 'stripes' },
  'sao paulo': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },
  'sarmiento junin': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'sarre union': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'sarreguemines': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'savigneux montbrison': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'sawbridgeworth town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'sc paderborn 07': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'scarborough athletic': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'scmano': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'scunthorpe': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'sd logrocs': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'sd negreira': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'seaford town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'seattle sounders': { primary: '#003399', secondary: '#009639', type: 'plain' },
  'seignosse capbreton': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'sektzia nes tziona': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'selongey': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'servette fc': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'sevenoaks town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'seyssinet pariset': { primary: '#003399', secondary: '#FFFFFF', type: 'plain' },
  'sg sonnenhof grossaspach': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'shaftesbury town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'shakhtar donetsk': { primary: '#FF6B00', secondary: '#000000', type: 'stripes' },
  'shamrock rovers': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'sheerwater': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'sheffield': { primary: '#E32221', secondary: '#FFFFFF', type: 'stripes' },
  'sheffield utd': { primary: '#E32221', secondary: '#FFFFFF', type: 'stripes' },
  'sheffield united': { primary: '#E32221', secondary: '#FFFFFF', type: 'stripes' },
  'sheffield wednesday': { primary: '#000000', secondary: '#F9D616', type: 'stripes' },
  'shelbourne': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'sheppey united': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'shepshed dynamo': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'shepton mallet': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'sherborne town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'sheriff tiraspol': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'sherwood colliery': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'shifnal town fc': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'shikun hamizrah': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'shildon afc': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'shimshon tel aviv': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'shkendija': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'sholing': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'shoreham': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'shrewsbury': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'siauliai': { primary: '#0066B3', secondary: '#FFFFFF', type: 'plain' },
  'sidmouth town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'sigma olomouc': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'sileby rangers': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'sileks': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'silkeborg': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'silsden': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'sittingbourne': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'sjk': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'skegness town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'slask wroclaw': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'slavia praha': { primary: '#D7143A', secondary: '#FFFFFF', type: 'half-and-half' },
  'sliema wanderers': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'slimbridge': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'slough town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'slovan bratislava': { primary: '#5BA3DC', secondary: '#FFFFFF', type: 'plain' },
  'snodland town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'soa': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'sochaux': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'soham town rangers': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'sol': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'solihull moors': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'south liverpool': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'south park': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'south shields': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'southall': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'southampton': { primary: '#D71920', secondary: '#130C0E', type: 'stripes' },
  'southend': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'southport': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'spaeri': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'spalding united': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'sparta praha': { primary: '#8C1823', secondary: '#FFFFFF', type: 'plain' },
  'spartak trnava': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'spennymoor town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'spezia': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'sport recife': { primary: '#E32221', secondary: '#000000', type: 'plain' },
  'sportfreunde lotte': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'sporting bengal united': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'sporting ceuta': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'sporting club inkberrow': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'sporting cp': { primary: '#00805D', secondary: '#FFFFFF', type: 'hoops' },
  'sporting gijon': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'sporting kansas city': { primary: '#003399', secondary: '#F9D616', type: 'plain' },
  'sporting khalsa': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'spvgg greuther furth': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'ss allinges': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'ssv jahn regensburg': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'ssv ulm 1846': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'st albans city': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'st austell': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'st blazey': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'st helens': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'st ives town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'st joseph s fc': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'st maur lusitanos': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'st mirren': { primary: '#000000', secondary: '#F9D616', type: 'stripes' },
  'st neots town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'st. louis city': { primary: '#CE1126', secondary: '#006038', type: 'plain' },
  'stade briochin': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'stade bthunois': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'stade plabennec': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'stade quimperlois': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'stafford rangers': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'stalybridge celtic': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'stamford': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'stansfeld': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'stanway rovers': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'steenvoorde': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'stella': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'stevenage': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'steyning town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'stjarnan': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'stockport county': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'stockport town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'stocksbridge park steels': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'stockton town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'stoke city': { primary: '#FFFFFF', secondary: '#E32221', type: 'plain' },
  'stone old alleynians': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'stotfold': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'stourbridge': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'stourport swifts': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'stowmarket town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'strasbourg koenigshoffen': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'stratford town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'street': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'struga': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'studley': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'sudtirol': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'sumqayct': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'sutjeska': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'sutton athletic': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'sutton coldfield town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'sutton common rovers': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'sutton utd': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'sv darmstadt 98': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'sv elversberg': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'sv sandhausen': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'sv wehen': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'swansea': { primary: '#FFFFFF', secondary: '#000000', type: 'plain' },
  'swindon supermarine': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'swindon town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'tadcaster albion': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'tadley calleva': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'takeley': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'talleres cordoba': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'tallinna kalev': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'tamworth': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'tarazona': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'taunton town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'tavistock': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'telstar': { primary: '#F9D616', secondary: '#E32221', type: 'plain' },
  'tenerife': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'ternana': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'teruel': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'textil escudo': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'thackley': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'thame united': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'thatcham town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'the new saints': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'thetford town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'thionville lusitanos': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'thonon cvian': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'thornaby': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'thornbury town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'three bridges': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'tigre': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'tigres uanl': { primary: '#F9D616', secondary: '#003399', type: 'plain' },
  'tikves': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },
  'tilbury': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'tirana': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'tiverton town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'tividale': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'toledo': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'toluca': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },
  'tonbridge angels': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'tondela': { primary: '#F9D616', secondary: '#009639', type: 'plain' },
  'tooting & mitcham united': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'torcy': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'toronto fc': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },
  'torpedo kutaisi': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'torpedo zhodino': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'torpoint athletic': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'torquay': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'torrent': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'torres': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'toulouse': { primary: '#482E92', secondary: '#FFFFFF', type: 'plain' },
  'tower hamlets': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'trafford': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'tranmere': { primary: '#FFFFFF', secondary: '#E32221', type: 'plain' },
  'transinvest vilnius': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'tre fiori': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'tre penne': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'trelissac': { primary: '#0066B3', secondary: '#FFFFFF', type: 'plain' },
  'tring athletic': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'tromso': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'tropezn': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'truro city': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'tsc backa topola': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'tuffley rovers': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'tunbridge wells': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'twente': { primary: '#E32221', secondary: '#000000', type: 'plain' },
  'tzeirei umm al fahm': { primary: '#F9D616', secondary: '#003B7A', type: 'plain' },
  'u.n.a.m. pumas': { primary: '#F9D616', secondary: '#002654', type: 'plain' },
  'ucam murcia': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'ud logrocs': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'ud ourense': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'ue santa coloma': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'uf touraine': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'una strassen': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'union saint jean': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },
  'union santa fe': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'union st. gilloise': { primary: '#F9D616', secondary: '#003399', type: 'stripes' },
  'united of manchester': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'universitario fc': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'universitatea cluj': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'universitatea craiova': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'us chateau malo': { primary: '#003399', secondary: '#FFFFFF', type: 'plain' },
  'us tchologo': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'utebo': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'utrecht': { primary: '#E32221', secondary: '#FFFFFF', type: 'stripes' },
  'uttoxeter town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'uxbridge': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'valladolid': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'valle egus': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'valur reykjavik': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'vancouver whitecaps': { primary: '#003399', secondary: '#FFFFFF', type: 'plain' },
  'vardar skopje': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'varndeanians': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'vasco da gama': { primary: '#000000', secondary: '#FFFFFF', type: 'stripes' },
  'vauxhall motors': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'vcd athletic': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'velez': { primary: '#0066B3', secondary: '#FFFFFF', type: 'plain' },
  'velez sarsfield': { primary: '#003399', secondary: '#FFFFFF', type: 'plain' },
  'vende fontenay': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'venezia': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'vfb lubeck': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'vfl bochum': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'vicenza virtus': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'viking': { primary: '#002F6C', secondary: '#FFFFFF', type: 'plain' },
  'vikingur gota': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'vikingur reykjavik': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'villeneuve la garenne': { primary: '#003399', secondary: '#FFFFFF', type: 'plain' },
  'virginia water': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'virtus': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'virtus entella': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'vitr': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'vizela': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },
  'vllaznia shkodcr': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'vojvodina': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'vps': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'wallingford & crowmarsh': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'walsall': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'walsham le willows': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'waltham abbey': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'walthamstow': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'walton & hersham': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'ware': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'warrington town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'watford': { primary: '#F9D616', secondary: '#E32221', type: 'plain' },
  'wealdstone': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'welling united': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'wellingborough town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'wellington afc': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'welwyn': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'welwyn garden city': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'west allotment celtic': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'west auckland town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'west brom': { primary: '#122F67', secondary: '#FFFFFF', type: 'stripes' },
  'west didsbury & chorlton': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'west essex': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'westbury united': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'westfield (surrey)': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'westfields': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'weston super mare': { primary: '#003B7A', secondary: '#FFFFFF', type: 'plain' },
  'weymouth': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'whickham': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'whitby town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'whitchurch alport': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'white ensign': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'whitehawk': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'whitley bay': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'whitstable town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'whyteleafe': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'wick': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'widnes': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'wigan': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'willand rovers': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'wimborne town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'wincanton town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'winchester city': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'windsor & eton': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'wingate & finchley': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'winsford united': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'winslow united': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'wisbech town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'wisla krakow': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'witham town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'wittenheim': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'witton albion': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'woking': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'wokingham town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'wombwell town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'woodbridge town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'woodford town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'worcester city': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'worcester raiders': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'workington': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'worksop town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'wormley rovers': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'worthing': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'wrexham': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },
  'wroxham': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'wycombe': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'wythenshawe amateurs': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'wythenshawe town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'yate town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'yaxley': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'yeovil town': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'york': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'yvetot': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'zaragoza': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'zeljeznicar sarajevo': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'zfc meuselwitz': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'zilina': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },
  'zimbru': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'zira': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'zoman': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },
  'zrinjski': { primary: '#6B7280', secondary: '#9CA3AF', type: 'plain' },

  // ---------- FIFA World Cup 2026 — National Teams (48 teams) ----------
  // Keys must match the normalized team name returned by the API (lowercase, no diacritics).
  // Colors reflect official 2026 home kit designs. New types used: pinstripes, sash, shoulder-panel, half-and-half.
  'algeria': { primary: '#FFFFFF', secondary: '#007749', type: 'plain' },
  'argentina': { primary: '#74ACDF', secondary: '#FFFFFF', type: 'pinstripes' },
  'australia': { primary: '#FFD700', secondary: '#006400', type: 'shoulder-panel' },
  'austria': { primary: '#ED2939', secondary: '#FFFFFF', type: 'hoops' },
  'belgium': { primary: '#000000', secondary: '#EF3340', type: 'shoulder-panel' },
  'bolivia': { primary: '#D52B1E', secondary: '#007A3D', type: 'plain' },
  'bosnia and herzegovina': { primary: '#002395', secondary: '#FFCD00', type: 'stripes' },
  'brazil': { primary: '#FEDD00', secondary: '#009C3B', type: 'plain' },
  'cameroon': { primary: '#007A5E', secondary: '#CE1126', type: 'plain' },
  'canada': { primary: '#FF0000', secondary: '#FFFFFF', type: 'plain' },
  'cape verde': { primary: '#003893', secondary: '#CF2027', type: 'stripes' },
  'chile': { primary: '#D52B1E', secondary: '#FFFFFF', type: 'plain' },
  'colombia': { primary: '#FFD100', secondary: '#003087', type: 'plain' },
  'congo dr': { primary: '#007FFF', secondary: '#FFD700', type: 'plain' },
  'costa rica': { primary: '#002B7F', secondary: '#CE1126', type: 'stripes' },
  'croatia': { primary: '#FF0000', secondary: '#FFFFFF', type: 'quarters' },
  'czech republic': { primary: '#D7141A', secondary: '#FFFFFF', type: 'plain' },
  'denmark': { primary: '#C60C30', secondary: '#FFFFFF', type: 'plain' },
  'ecuador': { primary: '#FFD100', secondary: '#003087', type: 'plain' },
  'egypt': { primary: '#CE1126', secondary: '#FFFFFF', type: 'plain' },
  'england': { primary: '#FFFFFF', secondary: '#CF081F', type: 'plain' },
  'france': { primary: '#002395', secondary: '#FFFFFF', type: 'plain' },
  'germany': { primary: '#FFFFFF', secondary: '#000000', type: 'shoulder-panel' },
  'ghana': { primary: '#FFFFFF', secondary: '#006B3F', type: 'plain' },
  'haiti': { primary: '#00209F', secondary: '#D21034', type: 'plain' },
  'honduras': { primary: '#0073CF', secondary: '#FFFFFF', type: 'plain' },
  'iran': { primary: '#239F40', secondary: '#FFFFFF', type: 'plain' },
  'iraq': { primary: '#CE1126', secondary: '#FFFFFF', type: 'plain' },
  'ivory coast': { primary: '#F77F00', secondary: '#009A44', type: 'plain' },
  'jamaica': { primary: '#000000', secondary: '#FFD700', type: 'plain' },
  'japan': { primary: '#003087', secondary: '#FFFFFF', type: 'plain' },
  'jordan': { primary: '#FFFFFF', secondary: '#007A3D', type: 'plain' },
  'korea republic': { primary: '#CE1126', secondary: '#FFFFFF', type: 'plain' },
  'mexico': { primary: '#006847', secondary: '#FFFFFF', type: 'plain' },
  'morocco': { primary: '#C1272D', secondary: '#006233', type: 'plain' },
  'netherlands': { primary: '#FF6600', secondary: '#FFFFFF', type: 'plain' },
  'new zealand': { primary: '#FFFFFF', secondary: '#000000', type: 'plain' },
  'nigeria': { primary: '#008751', secondary: '#FFFFFF', type: 'plain' },
  'norway': { primary: '#EF2B2D', secondary: '#FFFFFF', type: 'plain' },
  'panama': { primary: '#DB0921', secondary: '#002366', type: 'plain' },
  'paraguay': { primary: '#D52B1E', secondary: '#FFFFFF', type: 'stripes' },
  'peru': { primary: '#FFFFFF', secondary: '#D91023', type: 'sash' },
  'poland': { primary: '#FFFFFF', secondary: '#DC143C', type: 'plain' },
  'portugal': { primary: '#006600', secondary: '#FF0000', type: 'plain' },
  'qatar': { primary: '#8D1B3D', secondary: '#FFFFFF', type: 'plain' },
  'saudi arabia': { primary: '#006C35', secondary: '#FFFFFF', type: 'plain' },
  'scotland': { primary: '#003B6F', secondary: '#FFFFFF', type: 'plain' },
  'senegal': { primary: '#FFFFFF', secondary: '#00853F', type: 'plain' },
  'serbia': { primary: '#C6363C', secondary: '#FFFFFF', type: 'plain' },
  'south africa': { primary: '#007A4D', secondary: '#FFB612', type: 'plain' },
  'spain': { primary: '#C60B1E', secondary: '#FFD700', type: 'plain' },
  'sweden': { primary: '#006AA7', secondary: '#FECC02', type: 'plain' },
  'switzerland': { primary: '#FF0000', secondary: '#FFFFFF', type: 'plain' },
  'tunisia': { primary: '#E70013', secondary: '#FFFFFF', type: 'plain' },
  'turkey': { primary: '#E30A17', secondary: '#FFFFFF', type: 'plain' },
  'ukraine': { primary: '#005BBB', secondary: '#FFD500', type: 'half-and-half' },
  'united states': { primary: '#002868', secondary: '#BF0A30', type: 'shoulder-panel' },
  'uruguay': { primary: '#5EB6E4', secondary: '#FFFFFF', type: 'plain' },
  'uzbekistan': { primary: '#1EB53A', secondary: '#FFFFFF', type: 'plain' },
  'venezuela': { primary: '#CF142B', secondary: '#002868', type: 'plain' },

  // ---------- National team name aliases (API returns different casing/wording) ----------
  'south korea': { primary: '#CE1126', secondary: '#FFFFFF', type: 'plain' },
  'turkiye': { primary: '#E30A17', secondary: '#FFFFFF', type: 'plain' },
  'usa': { primary: '#002868', secondary: '#BF0A30', type: 'shoulder-panel' },
  'bosnia & herzegovina': { primary: '#002395', secondary: '#FFCD00', type: 'stripes' },
  'cape verde islands': { primary: '#003893', secondary: '#CF2027', type: 'stripes' },
  'curacao': { primary: '#003EA8', secondary: '#F9D616', type: 'plain' },

  // ---------- Additional clubs ----------
  'rakow czestochowa': { primary: '#D52B1E', secondary: '#FFFFFF', type: 'stripes' },
  'mazatlan': { primary: '#6B21A8', secondary: '#000000', type: 'plain' },
  'u.n.a.m. - pumas': { primary: '#F9D616', secondary: '#002654', type: 'plain' },

  // ---------- J1 League (Japan) ----------
  'avispa fukuoka': { primary: '#003087', secondary: '#000000', type: 'plain' },
  'cerezo osaka': { primary: '#E75480', secondary: '#000000', type: 'plain' },
  'fagiano okayama': { primary: '#1C3A6E', secondary: '#C41230', type: 'plain' },
  'fc tokyo': { primary: '#003087', secondary: '#E32221', type: 'plain' },
  'gamba osaka': { primary: '#002F6C', secondary: '#000000', type: 'plain' },
  'jef united chiba': { primary: '#F07B16', secondary: '#006B3F', type: 'plain' },
  'kashima': { primary: '#E32221', secondary: '#000000', type: 'plain' },
  'kashiwa reysol': { primary: '#FFD700', secondary: '#000000', type: 'plain' },
  'kawasaki frontale': { primary: '#6CB4EE', secondary: '#FFFFFF', type: 'plain' },
  'kyoto sanga': { primary: '#5B21B6', secondary: '#000000', type: 'plain' },
  'machida zelvia': { primary: '#1A6B3C', secondary: '#000000', type: 'plain' },
  'mito hollyhock': { primary: '#003B7A', secondary: '#FFFFFF', type: 'plain' },
  'nagoya grampus': { primary: '#E32221', secondary: '#000000', type: 'plain' },
  'sanfrecce hiroshima': { primary: '#3B1F7D', secondary: '#000000', type: 'plain' },
  'shimizu s pulse': { primary: '#F37321', secondary: '#003087', type: 'plain' },
  'tokyo verdy': { primary: '#00873C', secondary: '#000000', type: 'plain' },
  'urawa': { primary: '#E32221', secondary: '#000000', type: 'plain' },
  'v varen nagasaki': { primary: '#F37321', secondary: '#000000', type: 'plain' },
  'vissel kobe': { primary: '#CE1126', secondary: '#000000', type: 'plain' },
  'yokohama f. marinos': { primary: '#002868', secondary: '#E32221', type: 'stripes' },

  // ---------- German clubs (DFB Pokal / 2. Bundesliga) ----------
  'fc saarbrucken': { primary: '#003087', secondary: '#000000', type: 'plain' },
  'fc viktoria koln': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },
  'fortuna dusseldorf': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },
  'preußen munster': { primary: '#003B7A', secondary: '#FFFFFF', type: 'plain' },
  'rot weiß essen': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },
  'spvgg greuther furth': { primary: '#006B3F', secondary: '#FFFFFF', type: 'plain' },
  'vfb lubeck': { primary: '#003B7A', secondary: '#FFFFFF', type: 'plain' },

  // ---------- Spanish clubs (Copa del Rey) ----------
  'cacereno': { primary: '#009639', secondary: '#FFFFFF', type: 'plain' },
  'castellon': { primary: '#000000', secondary: '#FFFFFF', type: 'stripes' },
  'constancia': { primary: '#E32221', secondary: '#003B7A', type: 'plain' },
  'la union atletico': { primary: '#003B7A', secondary: '#FFFFFF', type: 'plain' },
  'merida ad': { primary: '#E32221', secondary: '#F9D616', type: 'plain' },
  'naxara': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },
  'real avila': { primary: '#000000', secondary: '#FFFFFF', type: 'plain' },
  'real aviles': { primary: '#003B7A', secondary: '#FFFFFF', type: 'plain' },
  'real jaen': { primary: '#F9D616', secondary: '#009639', type: 'plain' },
  'rsd alcala': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },
  'samano': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },
  'sd logrones': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },
  'tropezon': { primary: '#FFFFFF', secondary: '#003B7A', type: 'plain' },
  'ud logrones': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },
  'valle egues': { primary: '#003B7A', secondary: '#FFFFFF', type: 'plain' },

  // ---------- European cup clubs ----------
  'atletic club descaldes': { primary: '#003B7A', secondary: '#F9D616', type: 'plain' },
  'banik ostrava': { primary: '#003B7A', secondary: '#FFFFFF', type: 'plain' },
  'egnatia rrogozhine': { primary: '#E32221', secondary: '#000000', type: 'plain' },
  'hb torshavn': { primary: '#003B7A', secondary: '#FFFFFF', type: 'plain' },
  'inter club descaldes': { primary: '#003B7A', secondary: '#000000', type: 'plain' },
  'rigas fs': { primary: '#E32221', secondary: '#003B7A', type: 'plain' },
  'st patricks athl.': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },
  'vllaznia shkoder': { primary: '#E32221', secondary: '#003B7A', type: 'plain' },

  // ---------- Israeli clubs ----------
  'hapoel raanana': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },
  'ironi modiin': { primary: '#F9D616', secondary: '#003B7A', type: 'plain' },
  'maccabi neve shaanan': { primary: '#F9D616', secondary: '#003B7A', type: 'plain' },

  // ---------- English FA Cup clubs ----------
  'bishops cleeve': { primary: '#E32221', secondary: '#000000', type: 'plain' },
  'bishops stortford': { primary: '#003B7A', secondary: '#FFFFFF', type: 'plain' },
  'kings lynn town': { primary: '#003B7A', secondary: '#F9D616', type: 'plain' },

  // ---------- French cup clubs ----------
  'bassin darcachon sud': { primary: '#003B7A', secondary: '#FFFFFF', type: 'plain' },
  'boe bon encontre': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },
  'chamalieres': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },
  'dinan lehon': { primary: '#003B7A', secondary: '#FFFFFF', type: 'plain' },
  'les sables dolonne': { primary: '#003B7A', secondary: '#F9D616', type: 'plain' },
  'letrat la tour': { primary: '#009639', secondary: '#FFFFFF', type: 'plain' },
  'nord lozere': { primary: '#009639', secondary: '#FFFFFF', type: 'plain' },
  'perigny': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },
  'pledran': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },
  'racing besancon': { primary: '#003B7A', secondary: '#E32221', type: 'plain' },
  'raon letape': { primary: '#003B7A', secondary: '#FFFFFF', type: 'plain' },
  'sable': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },
  'saint colomban locmine': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },
  'sa merignac': { primary: '#003B7A', secondary: '#E32221', type: 'plain' },
  'stade bethunois': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },
  'thonon evian': { primary: '#009639', secondary: '#FFFFFF', type: 'plain' },
  'vendee fontenay': { primary: '#009639', secondary: '#FFFFFF', type: 'plain' },
  'villars lisle': { primary: '#003B7A', secondary: '#FFFFFF', type: 'plain' },
  'vitre': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },

  // ---------- K League 1 (South Korea) ----------
  'jeonbuk hyundai motors': { primary: '#006633', secondary: '#000000', type: 'plain' },
  'ulsan hd': { primary: '#003B7A', secondary: '#F9D616', type: 'plain' },
  'pohang steelers': { primary: '#E32221', secondary: '#000000', type: 'plain' },
  'jeju united': { primary: '#F37321', secondary: '#000000', type: 'plain' },
  'suwon fc': { primary: '#003B7A', secondary: '#FFFFFF', type: 'plain' },
  'gimcheon sangmu': { primary: '#003B7A', secondary: '#FFFFFF', type: 'plain' },
  'fc seoul': { primary: '#E32221', secondary: '#000000', type: 'plain' },
  'daejeon citizen': { primary: '#6B21A8', secondary: '#FFFFFF', type: 'plain' },
  'incheon united': { primary: '#003B7A', secondary: '#FFFFFF', type: 'plain' },
  'gwangju fc': { primary: '#F9D616', secondary: '#000000', type: 'plain' },
  'daegu fc': { primary: '#003B7A', secondary: '#FFFFFF', type: 'plain' },
  'gangwon fc': { primary: '#1C3A6E', secondary: '#FFFFFF', type: 'plain' },

  // ---------- A-League (Australia/NZ) ----------
  'adelaide united': { primary: '#E32221', secondary: '#003B7A', type: 'plain' },
  'auckland fc': { primary: '#002B5C', secondary: '#FFFFFF', type: 'plain' },
  'brisbane roar': { primary: '#F37321', secondary: '#000000', type: 'plain' },
  'central coast mariners': { primary: '#F9D616', secondary: '#003B7A', type: 'plain' },
  'macarthur fc': { primary: '#002B5C', secondary: '#F9D616', type: 'plain' },
  'melbourne city': { primary: '#6CB4EE', secondary: '#FFFFFF', type: 'plain' },
  'melbourne victory': { primary: '#002B5C', secondary: '#FFFFFF', type: 'plain' },
  'newcastle jets': { primary: '#003B7A', secondary: '#F9D616', type: 'plain' },
  'newcastle united jets': { primary: '#003B7A', secondary: '#F9D616', type: 'plain' },
  'perth glory': { primary: '#6B21A8', secondary: '#F9D616', type: 'plain' },
  'sydney fc': { primary: '#003399', secondary: '#FFFFFF', type: 'plain' },
  'wellington phoenix': { primary: '#F9D616', secondary: '#000000', type: 'plain' },
  'western sydney wanderers': { primary: '#000000', secondary: '#E32221', type: 'stripes' },
  'western united': { primary: '#1C3A6E', secondary: '#F9D616', type: 'plain' },

  // ---------- MLS additional clubs ----------
  'austin fc': { primary: '#00B140', secondary: '#000000', type: 'plain' },
  'charlotte fc': { primary: '#1A85C8', secondary: '#FFFFFF', type: 'plain' },
  'la galaxy': { primary: '#002B5C', secondary: '#F9D616', type: 'plain' },
  'san diego fc': { primary: '#003087', secondary: '#F37321', type: 'plain' },

  // ---------- Saudi Pro League additional clubs ----------
  'al qadsiah': { primary: '#F9D616', secondary: '#000000', type: 'plain' },
  'al taawoun': { primary: '#F9D616', secondary: '#FFFFFF', type: 'plain' },
  'al wehda': { primary: '#009639', secondary: '#FFFFFF', type: 'plain' },
  'abha': { primary: '#009639', secondary: '#FFFFFF', type: 'plain' },
  'al batin': { primary: '#E32221', secondary: '#000000', type: 'plain' },

  // ---------- Brasileirao additional clubs ----------
  'atletico mineiro': { primary: '#000000', secondary: '#FFFFFF', type: 'plain' },
  'criciuma ec': { primary: '#F9D616', secondary: '#000000', type: 'plain' },

  // ---------- Allsvenskan (Sweden) ----------
  'degerfors if': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },
  'gais': { primary: '#006B3F', secondary: '#000000', type: 'plain' },
  'halmstad': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },
  'if brommapojkarna': { primary: '#000000', secondary: '#FFFFFF', type: 'plain' },
  'ifk goteborg': { primary: '#003B7A', secondary: '#FFFFFF', type: 'plain' },
  'kalmar ff': { primary: '#F9D616', secondary: '#000000', type: 'stripes' },
  'mjallby aif': { primary: '#F9D616', secondary: '#000000', type: 'plain' },
  'orgryte is': { primary: '#FFFFFF', secondary: '#003B7A', type: 'plain' },
  'sirius': { primary: '#003B7A', secondary: '#FFFFFF', type: 'plain' },
  'vasteras sk fk': { primary: '#F9D616', secondary: '#000000', type: 'plain' },

  // ---------- Superliga (Denmark) ----------
  'aarhus': { primary: '#FFFFFF', secondary: '#6B21A8', type: 'stripes' },
  'fc fredericia': { primary: '#003B7A', secondary: '#FFFFFF', type: 'plain' },
  'fc nordsjaelland': { primary: '#E32221', secondary: '#000000', type: 'plain' },
  'odense': { primary: '#FFFFFF', secondary: '#003B7A', type: 'plain' },
  'randers fc': { primary: '#E32221', secondary: '#000000', type: 'plain' },
  'sonderjyske': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },
  'vejle': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },
  'viborg': { primary: '#009639', secondary: '#F9D616', type: 'plain' },

  // ---------- Ekstraklasa (Poland) ----------
  'arka gdynia': { primary: '#F9D616', secondary: '#003B7A', type: 'plain' },
  'cracovia krakow': { primary: '#E32221', secondary: '#FFFFFF', type: 'stripes' },
  'gks katowice': { primary: '#F9D616', secondary: '#000000', type: 'plain' },
  'gornik zabrze': { primary: '#000000', secondary: '#F9D616', type: 'plain' },
  'korona kielce': { primary: '#F9D616', secondary: '#000000', type: 'plain' },
  'lechia gdansk': { primary: '#FFFFFF', secondary: '#009639', type: 'plain' },
  'motor lublin': { primary: '#F9D616', secondary: '#009639', type: 'plain' },
  'nieciecza': { primary: '#F9D616', secondary: '#003B7A', type: 'plain' },
  'piast gliwice': { primary: '#003B7A', secondary: '#FFFFFF', type: 'plain' },
  'pogon szczecin': { primary: '#E32221', secondary: '#003B7A', type: 'plain' },
  'radomiak radom': { primary: '#009639', secondary: '#FFFFFF', type: 'plain' },
  'widzew łodz': { primary: '#E32221', secondary: '#FFFFFF', type: 'stripes' },
  'wisla plock': { primary: '#003B7A', secondary: '#FFFFFF', type: 'plain' },
  'zaglebie lubin': { primary: '#000000', secondary: '#F9D616', type: 'plain' },

  // ---------- Super League 1 (Greece) ----------
  'asteras tripolis': { primary: '#F9D616', secondary: '#000000', type: 'plain' },
  'atromitos': { primary: '#003B7A', secondary: '#FFFFFF', type: 'plain' },
  'kifisia': { primary: '#003B7A', secondary: '#FFFFFF', type: 'plain' },
  'larisa': { primary: '#003B7A', secondary: '#FFFFFF', type: 'plain' },
  'levadiakos': { primary: '#009639', secondary: '#FFFFFF', type: 'plain' },
  'ofi': { primary: '#F9D616', secondary: '#000000', type: 'plain' },
  'panetolikos': { primary: '#009639', secondary: '#FFFFFF', type: 'plain' },
  'panserraikos': { primary: '#003B7A', secondary: '#FFFFFF', type: 'plain' },
  'volos nfc': { primary: '#F9D616', secondary: '#000000', type: 'plain' },

  // ---------- Jupiler Pro League (Belgium) ----------
  'beerschot va': { primary: '#6B21A8', secondary: '#FFFFFF', type: 'plain' },
  'dender': { primary: '#003B7A', secondary: '#F9D616', type: 'plain' },
  'kvc westerlo': { primary: '#F9D616', secondary: '#009639', type: 'plain' },
  'liege': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },
  'lommel united': { primary: '#003B7A', secondary: '#FFFFFF', type: 'plain' },
  'patro eisden': { primary: '#000000', secondary: '#FFFFFF', type: 'plain' },
  'raal la louviere': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },
  'st. truiden': { primary: '#F9D616', secondary: '#000000', type: 'plain' },
  'zulte waregem': { primary: '#003B7A', secondary: '#F9D616', type: 'plain' },

  // ---------- Swiss Super League ----------
  'fc aarau': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },
  'fc luzern': { primary: '#003B7A', secondary: '#FFFFFF', type: 'plain' },
  'fc sion': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },
  'fc thun': { primary: '#E32221', secondary: '#FFFFFF', type: 'plain' },
  'fc winterthur': { primary: '#003B7A', secondary: '#F9D616', type: 'plain' },
  'grasshoppers': { primary: '#003B7A', secondary: '#FFFFFF', type: 'plain' },

  // ---------- Austrian Bundesliga ----------
  'fc bw linz': { primary: '#003B7A', secondary: '#FFFFFF', type: 'plain' },
  'ried': { primary: '#000000', secondary: '#FFFFFF', type: 'stripes' },
  'scr altach': { primary: '#FFFFFF', secondary: '#000000', type: 'plain' },
  'tsv hartberg': { primary: '#F9D616', secondary: '#000000', type: 'plain' },
  'wsg wattens': { primary: '#FFFFFF', secondary: '#009639', type: 'plain' },

  // ---------- A-League name aliases (API omits FC suffix) ----------
  'auckland': { primary: '#002B5C', secondary: '#FFFFFF', type: 'plain' },
  'macarthur': { primary: '#002B5C', secondary: '#F9D616', type: 'plain' },
  'sydney': { primary: '#003399', secondary: '#FFFFFF', type: 'plain' },

  // ---------- K League 1 name aliases (API names differ) ----------
  'bucheon fc 1995': { primary: '#003B7A', secondary: '#FFFFFF', type: 'plain' },
  'fc anyang': { primary: '#003B7A', secondary: '#FFFFFF', type: 'plain' },
  'gimcheon sangmu fc': { primary: '#003B7A', secondary: '#FFFFFF', type: 'plain' },
  'jeju united fc': { primary: '#F37321', secondary: '#000000', type: 'plain' },
  'jeonbuk motors': { primary: '#006633', secondary: '#000000', type: 'plain' },
  'ulsan hyundai fc': { primary: '#003B7A', secondary: '#F9D616', type: 'plain' },

  // ---------- Brasileirao name aliases ----------
  'chapecoense sc': { primary: '#009639', secondary: '#FFFFFF', type: 'plain' },
  'coritiba': { primary: '#FFFFFF', secondary: '#009639', type: 'plain' },
  'remo': { primary: '#003B7A', secondary: '#FFFFFF', type: 'plain' },

  // ---------- Liga Profesional Argentina additional ----------
  'estudiantes de rio cuarto': { primary: '#003B7A', secondary: '#F9D616', type: 'plain' },
  'gimnasia m.': { primary: '#003B7A', secondary: '#FFFFFF', type: 'plain' },

}